import Dataset, { ROLLUPS_NOT_VIEWABLE } from 'scoreboard/datasets/model/dataset'
import _ from 'lodash'
import formatValue from 'scoreboard/components/format-value'
import i18n from 'scoreboard/components/i18n'
import { ELLIPSIS } from 'scoreboard/reports/report-constants'
import constants from 'scoreboard/components/constants'
import isGeographicOrRollup from 'scoreboard/datasets/is-geographic-or-rollup'
import KeyDisplay from 'scoreboard/datasets/key-display'

/**
 * Formats a value suitable for display to an end user. This delegates much of
 * its work to the formatValue function, but this one differs in that it also
 * sometimes generates HTML, and it handles more data types.
 *
 * @param {FormatValueOptions} options
 * @param {Dataset} options.dataset the dataset
 * @param {Number} options.fieldId the ID of the field this result is based on
 * @param {Object} options.treeItemsById a hash of tree IDs to tree items
 * @param {Boolean} [options.escape=true] whether to escape the value
 * @param {Boolean} [options.includeCommas=true] whether to include commas in
 * the output for "list unique values" aggregation types
 */
function formatHtmlValue(options) {
	if (options?.value === ROLLUPS_NOT_VIEWABLE) {
		return ROLLUPS_NOT_VIEWABLE
	}
	if (_.isArray(options?.value)) {
		if (isGeographicOrRollup(options.dataType)) {
			options.keyDisplay = new KeyDisplay({
				model: options.dataset,
				field: options.dataset.getUnderlyingField(options.fieldId),
				treeItemsById: options.treeItemsById
			})
		}

		return formatListUniqueValues(options)
	}
	return formatAndEscape(options)
}

/**
 * Forms a list using the "list unique values" aggregation type.
 * @param {FormatValueOptions} options
 * @param {Boolean} [options.escape=true] whether to escape the value
 * @param {Boolean} [options.includeCommas=true] whether to include commas in
 * the output
 */
function formatListUniqueValues(options) {
	const list = options?.value
	let includeCommas = options?.includeCommas ?? true
	const separator = includeCommas ? ', ' : ' '
	if (_.isNil(list) || _.isEmpty(list)) {
		return ''
	} else {
		const formattedList = list.map(value => {
			if (_.isNil(value) || value === Dataset.BLANK || value === '') {
				return '<span class="blank-value">' +
					i18n.get('common.blank') +
				'</span>'
			} else {
				const individualOptions = { ...options, value }
				return formatAndEscape(individualOptions)
			}
		})

		if (formattedList.length > constants.LIST_UNIQUE_VALUES_MAX) {
			return formattedList
				.slice(0, constants.LIST_UNIQUE_VALUES_MAX)
				.join(separator) + ELLIPSIS
		} else {
			return formattedList.join(separator)
		}
	}
}

/**
 * Formats a value and escapes it if necessary.
 *
 * @param {FormatValueOptions} options
 * @param {Boolean} [options.escape=true] whether to escape the value
 */
function formatAndEscape(options) {
	let formatted = formatValue(options)
	let escape = options.escape ?? true
	return escape ? _.escape(formatted) : formatted
}

export default formatHtmlValue
export { formatListUniqueValues }
formatHtmlValue.__filename__ = 'components/format-html-value.js'
